/* eslint-disable eqeqeq */
import { useEffect, useRef, useSyncExternalStore } from 'react';

/**
 * Phase 6e2a — THE §49.2 auto-save engine (base_classes/component_model.js,
 * 299 lines, read in full; PATCH behavior byte-faithful — parity 299–304).
 *
 * One FormModel per concrete component/accessory row. Views never call HTTP
 * for normal fields — they `model.set(...)` and the model persists (§19.2).
 *
 * Pipeline (onModelSync — armed after the first sync):
 *  - savedState = toJSON() snapshot; every change event:
 *  - BE-originated (info.__from_back_end | info.xhr | info.validate): NO save;
 *    render skipped when changed.rate, any accessory classID, or gas_heater
 *    without isSelect; else re-render after fullyLoading.
 *  - Edit-swallowing guards (NO save):
 *      (view.rate || changed.size_qty_name) unless blank_off/bulkhead gauge;
 *      fan without master_fan_id AND master_motor_id (unless bulkhead gauge);
 *      filter without master_filter_id (unless blank_off gauge);
 *      filter master_frame_id→null change while has_frame;
 *      uvc accessory entirely (classID model.accessory.uvc).
 *  - changedStack accumulation → 1000 ms setTimeout → chained behind syncing
 *    → merge stack → drop keys loosely-equal to savedState (`==` + Number()
 *    compare; STRICT compare when either side is 0 or '0') → always-drop
 *    motor_power_total/bp_kw_total/rating_motor_fla_total → abort when empty
 *    or fan with motor_nema_frame_id/redundancy_mode in changes ([NEG] — those
 *    never PATCH from this path).
 *    NOTE (code vs spec §49.2 text): the legacy computes `need_validate`
 *    for the esp-only case but does NOT abort — an esp-only batch IS PATCHed.
 *    Code wins (byte-faithful PATCH behavior).
 *  - Sticky adds: motor_voltage_id re-sent when set on model; coil
 *    air_stream_id w/o airflow_actual drags airflow_actual+airflow_std; coil
 *    always re-sends duty_id (savedState fallback); only_viewport +
 *    master_viewport_id:null pair dropped together.
 *  - gas_heater: the save block runs ONLY when `rate` ∈ changes (selector owns
 *    other writes). Legacy leaves the syncing promise unresolved in that case
 *    (queue stall); ported as a dropped batch — observable PATCH stream is
 *    identical (no PATCH either way), without the legacy deadlock.
 *  - save → PATCH {changed only}; flight: disableFilterFields. Success:
 *    savedState = response; checkChangesAndValidate; enableFilterFields
 *    (legacy ends with view.render()). Error: same finishers, NO ROLLBACK —
 *    savedState stays stale so the next change diff resurrects failed fields
 *    (sticky re-sends, §49.2 error path).
 *  - Validation routing (checkChangesAndValidate): changed ∩ validatedFields →
 *    needTunnelValidationFields list (verbatim) → validateTunnelOnlyComponents;
 *    accessory → validateAccessoryOnChange (+door_ids partner sweep — 6e2b);
 *    other → validateOnSectionChange. Else withRating &&
 *    componentSelectionInRateMode (armed by rating_mode:hide:<id> {selected})
 *    → tunnel validation (+ section_2 tunnel lookup).
 */

export interface SetInfo {
  /** syncdata socket push (§24) — echo guard. */
  __from_back_end?: boolean;
  /** change applied from an XHR response — echo guard. */
  xhr?: boolean;
  /** validation-pipeline write — echo guard. */
  validate?: boolean;
  /** Backbone silent set (no change event, no save). */
  silent?: boolean;
}

export type ChangeListener = (
  changed: Record<string, unknown>,
  info: SetInfo
) => void;

export interface FormModelOptions {
  id: number;
  classID: string;
  type: string;
  initialAttributes: Record<string, unknown>;
  validatedFields: string[];
  /** PATCH ahu/<plural>/<id> — resolves with the server row. */
  saveFn: (changes: Record<string, unknown>) => Promise<Record<string, unknown>>;
  /** §22 routing callbacks (validationController thunks, wired by the host). */
  validation: {
    validateTunnelOnlyComponents(sectionId: number): void;
    validateOnSectionChange(componentId: number): void;
    validateAccessoryOnChange(componentId: number): void;
  };
  /** view back-references the engine reads (§49.2 view.* touches). */
  viewRef: {
    rate: boolean;
    withRating: boolean;
    catalogueTargetFields: string[];
    disableFilterFields(): void;
    enableFilterFields(): void;
  };
  /**
   * 6e2b — §49.2 success-path accessory extras (component_model.js:160-188):
   * select:hood / tunnelLugs:recalcPlacement / conditional position.fetch /
   * sublist-grey lift. Called AFTER checkChangesAndValidate on SUCCESS only
   * (legacy error path skips the accessory block). Provided by
   * useAccessoryForm; absent for component forms (no behavior change).
   */
  accessoryAfterSave?: (changes: Record<string, unknown>) => void;
}

/** §49.2 checkChangesAndValidate — hardcoded list, copied verbatim
 * (master_frame_id duplicated in the source; harmless in a Set-less scan). */
const NEED_TUNNEL_VALIDATION_FIELDS = [
  'master_filter_id',
  'master_frame_id',
  'master_eliminator_id',
  'master_frame_id',
  'distance_from_section_ups',
  'master_media_id',
  'delim_depth_id',
  'master_pump_id',
  'fill_id',
  'drain_id',
  'overflow_id',
  'master_silencer_id',
  'length',
  'height_from_floor',
];

/** Always dropped from PATCH bodies (§49.2). */
const ALWAYS_DROPPED = new Set([
  'motor_power_total',
  'bp_kw_total',
  'rating_motor_fla_total',
]);

/** stateStorage.models equivalent — `_STORAGE_.models[classID][id]` (§19.1). */
const modelRegistry = new Map<string, FormModel>();

export const getRegisteredModel = (classID: string, id: number) =>
  modelRegistry.get(`${classID}:${id}`);

/** Loose-equality diff rule, verbatim (component_model.js:114). */
function looselyEqualToSaved(change: unknown, saved: unknown): boolean {
  if (change === 0 || change === '0' || saved === 0) return change === saved;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (change as any) == (saved as any) || Number(change) === Number(saved);
}

export class FormModel {
  readonly id: number;

  readonly classID: string;

  readonly type: string;

  attributes: Record<string, unknown>;

  savedState: Record<string, unknown>;

  /** Last change-event payload (Backbone `model.changed`). */
  changed: Record<string, unknown> = {};

  /** Armed by `rating_mode:hide:<id>` with res.selected (§49.2). */
  componentSelectionInRateMode = false;

  /**
   * DATA-SAFETY GATE (verify 6e2a-3 / builder deviation #6): saves are
   * STRUCTURALLY impossible until the user actually touches the form.
   * Init-time programmatic churn (catalogue filter seeding, auto-pick,
   * cross-filter wiring, render side effects) merges into `attributes` but
   * never reaches the PATCH pipeline. Legacy reaches the same observable
   * stability through the savedState loose-equality dedup (its init writes
   * recompute the stored values); the React port makes the guarantee
   * explicit so a count/order divergence can never mutate data on mount.
   * First user gesture (field commit / filter edit / reset / smart-list
   * pick / rating commit) flips the gate for the model's lifetime.
   */
  userArmed = false;

  markUserAction(): void {
    this.userArmed = true;
  }

  /** In-flight PATCH marker (legacy `pennding` [sic]). */
  pennding = false;

  /** Resolves when the current debounce+save batch settles (view awaits). */
  syncProcess: Promise<void> = Promise.resolve();

  private syncProcessFinisher: (() => void) | null = null;

  private validatedFields: Set<string>;

  private changedStack: Array<Record<string, unknown>> = [];

  private syncing: Promise<void> = Promise.resolve();

  private armed = false;

  private destroyed = false;

  private listeners = new Set<ChangeListener>();

  private subscribers = new Set<() => void>();

  private version = 0;

  private opts: FormModelOptions;

  constructor(opts: FormModelOptions) {
    this.opts = opts;
    this.id = opts.id;
    this.classID = opts.classID;
    this.type = opts.type;
    this.attributes = { ...opts.initialAttributes };
    this.savedState = { ...opts.initialAttributes };
    this.validatedFields = new Set(opts.validatedFields);
    modelRegistry.set(`${this.classID}:${this.id}`, this);
  }

  /** listenToOnce('sync') equivalent: arm the auto-save pipeline. */
  armAutoSave(): void {
    this.armed = true;
    this.savedState = { ...this.attributes };
  }

  /** forceSync() — re-arm for fresh-created models built without fetch. */
  forceSync(): void {
    this.armAutoSave();
  }

  get<T = unknown>(key: string): T {
    return this.attributes[key] as T;
  }

  toJSON(): Record<string, unknown> {
    return { ...this.attributes };
  }

  /** React subscription surface (useSyncExternalStore). */
  subscribe = (cb: () => void): (() => void) => {
    this.subscribers.add(cb);
    return () => this.subscribers.delete(cb);
  };

  getVersion = (): number => this.version;

  /** Backbone listenTo(model,'change') equivalent (shim member #2 surface). */
  onChange(listener: ChangeListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Backbone model.set: marks changed keys (strict-unequal — Backbone uses
   * _.isEqual; scalars only here), fires listeners, runs the §49.2 pipeline.
   */
  set(attrs: Record<string, unknown>, info: SetInfo = {}): void {
    if (this.destroyed) return;
    const changed: Record<string, unknown> = {};
    Object.keys(attrs).forEach((key) => {
      if (this.attributes[key] !== attrs[key]) changed[key] = attrs[key];
      this.attributes[key] = attrs[key];
    });
    if (!Object.keys(changed).length) return;
    this.changed = changed;
    this.version += 1;
    if (info.silent) return;
    this.subscribers.forEach((cb) => cb());
    this.listeners.forEach((l) => l(changed, info));
    if (this.armed) this.onChangeEvent(changed, info);
  }

  destroy(): void {
    this.destroyed = true;
    modelRegistry.delete(`${this.classID}:${this.id}`);
    this.listeners.clear();
    this.subscribers.clear();
  }

  // -------------------------------------------------------------------------
  // §49.2 pipeline
  // -------------------------------------------------------------------------
  private onChangeEvent(changed: Record<string, unknown>, info: SetInfo): void {
    const { viewRef } = this.opts;

    // BE-originated: never a save (echo guards §19.2). Render handled by the
    // chassis (subscribers already notified above).
    if (info.__from_back_end || info.xhr || info.validate) return;

    // Save-on-init structurally impossible: no user gesture yet → no PATCH
    // (verify 6e2a-3 — the eliminator mount auto-pick wrote real data).
    if (!this.userArmed) return;

    const has = (k: string) => Object.prototype.hasOwnProperty.call(changed, k);

    // ---- guard table (§49.2 / parity 300) ----
    if (
      (viewRef.rate || changed.size_qty_name) &&
      !has('blank_off_mtl_gauge_id') &&
      !has('bulkhead_wall_mtl_gauge_id')
    )
      return;
    if (
      this.classID === 'component.fan.model' &&
      !this.get('master_fan_id') &&
      !this.get('master_motor_id') &&
      !has('bulkhead_wall_mtl_gauge_id')
    )
      return;
    if (
      this.classID === 'component.filter.model' &&
      !this.get('master_filter_id') &&
      !has('blank_off_mtl_gauge_id')
    )
      return;
    if (
      this.classID === 'component.filter.model' &&
      !this.get('master_frame_id') &&
      has('master_frame_id') &&
      this.get('has_frame')
    )
      return;
    if (this.classID === 'model.accessory.uvc') return;

    this.changedStack.push({ ...changed });

    if (!this.syncProcessFinisher) {
      this.syncProcess = new Promise<void>((res) => {
        this.syncProcessFinisher = res;
      });
    }

    window.setTimeout(() => {
      void this.syncing.then(() => {
        let finish: () => void = () => undefined;
        this.syncing = new Promise<void>((res) => {
          finish = res;
        });

        // merge stack
        const changes: Record<string, unknown> = {};
        while (this.changedStack.length) {
          Object.assign(changes, this.changedStack.shift());
        }

        // loose-equality diff vs savedState + always-dropped keys
        Object.keys(changes).forEach((key) => {
          if (
            looselyEqualToSaved(changes[key], this.savedState[key]) ||
            ALWAYS_DROPPED.has(key)
          ) {
            delete changes[key];
          }
        });

        // abort: empty, or fan motor_nema_frame_id/redundancy_mode ([NEG])
        if (
          !Object.keys(changes).length ||
          (this.classID === 'component.fan.model' &&
            (changes.motor_nema_frame_id || changes.redundancy_mode))
        ) {
          finish();
          this.finishSyncProcess();
          return;
        }

        // sticky: motor_voltage_id re-sent when set on the model
        if (this.get('motor_voltage_id') && !changes.motor_voltage_id) {
          changes.motor_voltage_id = this.get('motor_voltage_id');
        }

        // gas_heater: only `rate` batches save (selector-owned writes —
        // legacy stalls the queue here; ported as a dropped batch).
        if (
          this.classID === 'component.gas_heater.model' &&
          !Object.prototype.hasOwnProperty.call(changes, 'rate')
        ) {
          finish();
          this.finishSyncProcess();
          return;
        }

        // coil stickies
        if (
          this.classID === 'component.coil.model' &&
          changes.air_stream_id &&
          !changes.airflow_actual
        ) {
          changes.airflow_actual = this.get('airflow_actual');
          changes.airflow_std = this.get('airflow_std');
        }
        if (this.classID === 'component.coil.model' && !changes.duty_id) {
          changes.duty_id = this.savedState.duty_id;
        }

        // only_viewport / master_viewport_id pair-drop
        if (changes.only_viewport && changes.master_viewport_id === null) {
          delete changes.only_viewport;
          delete changes.master_viewport_id;
        }

        viewRef.disableFilterFields();
        this.pennding = true;

        this.opts
          .saveFn(changes)
          .then((response) => {
            this.pennding = false;
            // success: accept server state (xhr echo set, no re-save)
            this.set(response, { xhr: true });
            this.savedState = this.toJSON();
            this.checkChangesAndValidate(changes);
            // §49.2 accessory success extras (component_model.js:160-188).
            if (this.opts.accessoryAfterSave) this.opts.accessoryAfterSave(changes);
            finish();
            this.finishSyncProcess();
            viewRef.enableFilterFields();
          })
          .catch(() => {
            // error path: NO ROLLBACK — savedState stays stale so the next
            // diff re-sends the failed fields (§49.2).
            this.pennding = false;
            this.checkChangesAndValidate(changes);
            finish();
            this.finishSyncProcess();
            viewRef.enableFilterFields();
          });
      });
    }, 1000);
  }

  private finishSyncProcess(): void {
    if (this.syncProcessFinisher) {
      this.syncProcessFinisher();
      this.syncProcessFinisher = null;
      this.syncProcess = Promise.resolve();
    }
  }

  /** §49.2 validation routing (parity 303). */
  private checkChangesAndValidate(changes: Record<string, unknown>): void {
    const { validation } = this.opts;
    const keys = Object.keys(changes);
    const isAccessory = this.classID.includes('model.accessory');

    if (keys.some((k) => this.validatedFields.has(k))) {
      if (
        keys.some(
          (k) => NEED_TUNNEL_VALIDATION_FIELDS.includes(k) && !isAccessory
        )
      ) {
        validation.validateTunnelOnlyComponents(Number(this.get('section_id')));
      } else if (isAccessory) {
        validation.validateAccessoryOnChange(this.id);
        // door_ids partner sweep — accessory forms (6e2b).
      } else {
        validation.validateOnSectionChange(this.id);
      }
    } else if (this.opts.viewRef.withRating) {
      if (this.componentSelectionInRateMode) {
        this.componentSelectionInRateMode = false;
        validation.validateTunnelOnlyComponents(Number(this.get('section_id')));
        // section_2 tunnel lookup handled by the caller-provided thunk (it
        // resolves tunnel ids from the RTKQ cache — validationController).
        if (this.get('section_2_id')) {
          validation.validateTunnelOnlyComponents(Number(this.get('section_2_id')));
        }
      }
    }
  }
}

/**
 * Hook wrapper: one FormModel per (classID,id) for the mounted form, with a
 * version-tick subscription so edits re-render the React form.
 */
export function useFormModel(
  factory: () => FormModel | null,
  deps: unknown[]
): FormModel | null {
  const ref = useRef<FormModel | null>(null);
  const depsRef = useRef<unknown[]>([]);

  const changedDeps =
    deps.length !== depsRef.current.length ||
    deps.some((d, i) => d !== depsRef.current[i]);
  if (changedDeps) {
    ref.current?.destroy();
    ref.current = factory();
    depsRef.current = deps;
  }

  useEffect(
    () => () => {
      ref.current?.destroy();
      ref.current = null;
      depsRef.current = [];
    },
    []
  );

  const model = ref.current;
  useSyncExternalStore(
    model ? model.subscribe : () => () => undefined,
    model ? model.getVersion : () => 0
  );
  return model;
}
